import * as Yup from "yup";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider, Form } from "formik";
import {
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "components/Iconify";
import { loginUser } from "redux/thunk/auth.thunk";
import { setTfaRequired } from "redux/slices/auth.slice";

export default function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const { systemInfo } = useSelector((state) => state.systemInfo);

  const LoginSchema = Yup.object().shape({
    emailOrUsername: Yup.string().required("Email or Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      emailOrUsername: "",
      password: "",
      remember: false,
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await dispatch(loginUser(values));

        if (res.type === "auth/login-user/fulfilled") {
          if (res.payload.twoFactorRequired) {
            // OTP required
            dispatch(
              setTfaRequired({
                required: true,
                userId: res.payload.userId,
                user: res.payload.user,
              })
            );
            navigate("/auth/verification", {
              state: { userId: res.payload.userId, user: res.payload.user },
            });
          } else {
            // Normal login flow
            navigate(
              res.payload.user?.role?.permissions?.includes("canViewDashboard")
                ? "/dashboard/app"
                : res.payload.user?.role?.home_page,
              { replace: true }
            );
          }

          resetForm();
          toast.success(res.payload.message);
        } else if (res.type === "auth/login-user/rejected") {
          toast.error(
            res?.payload?.error ||
              "Failed to connect due to network or server error"
          );
        }
      } catch (error) {
        toast.error(error.message || "An error occurred during login.");
      }
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="username"
            label="Username or Email address"
            {...getFieldProps("emailOrUsername")}
            error={Boolean(touched.emailOrUsername && errors.emailOrUsername)}
            helperText={touched.emailOrUsername && errors.emailOrUsername}
          />
          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                    <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          <FormControlLabel
            control={<Checkbox {...getFieldProps("remember")} checked={values.remember} />}
            label="Remember me"
          />
          <Link variant="subtitle2" underline="hover" href="/auth/forget-password">
            Forgot password?
          </Link>
        </Stack>

        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
